import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import platformService from '../services/platformService';
import TourismotionCredentialsFields from '../components/TourismotionCredentialsFields';

const PlatformEditScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    commission: '',
    apiKey: '',
    apiUsername: '',
    apiPassword: '',
    hasApiKey: false,
    hasApiPassword: false,
    supportsScheduler: false,
    color: '#000000',
    schedulerEnabled: false,
    schedulerCron: ''
  });

  useEffect(() => {
    let cancelled = false;

    const loadPlatform = async () => {
      try {
        setError('');
        const platform = await platformService.getPlatformById(id);
        if (platform && !cancelled) {
          setFormData({
            name: platform.name,
            description: platform.description || '',
            url: platform.url || '',
            commission: platform.commission || '',
            apiKey: '',
            apiUsername: platform.apiUsername || '',
            apiPassword: '',
            hasApiKey: platform.hasApiKey || false,
            hasApiPassword: platform.hasApiPassword || false,
            supportsScheduler: platform.supportsScheduler || false,
            color: platform.color || '#000000',
            schedulerEnabled: platform.schedulerEnabled || false,
            schedulerCron: platform.schedulerCron || ''
          });
        }
      } catch (err) {
        console.error('Error al cargar plataforma:', err);
        if (!cancelled) {
          setError('Error al cargar la plataforma. Por favor, intenta nuevamente.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPlatform();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      // Separar los datos del scheduler
      const {
        schedulerEnabled,
        schedulerCron,
        hasApiKey,
        hasApiPassword,
        supportsScheduler,
        ...platformData
      } = formData;

      if (!platformData.apiKey) delete platformData.apiKey;
      if (!platformData.apiPassword) delete platformData.apiPassword;

      platformData.schedulerEnabled = supportsScheduler ? schedulerEnabled : false;
      platformData.schedulerCron = schedulerCron;

      // Guardar datos y scheduler en una única operación
      await platformService.updatePlatform(id, platformData);

      navigate('/platforms');
    } catch (err) {
      console.error('Error al actualizar plataforma:', err);
      setError(err.response?.data?.message || 'Error al actualizar la plataforma. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const isTourismotion = formData.name.toLowerCase() === 'tourismotion';

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Editar Plataforma</h1>
      </div>

      {error && (
        <div className="alert alert-error" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="section-card max-w-2xl">
        <form onSubmit={handleSubmit} className="section-card-body space-y-6">
          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Nombre</span>
            </label>
            <input
              type="text"
              value={formData.name}
              disabled
              className="input input-bordered w-full bg-base-200"
            />
          </div>

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Descripción</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="textarea textarea-bordered w-full"
              rows="3"
            />
          </div>

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">URL</span>
            </label>
            <input
              type="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              placeholder="https://ejemplo.com"
            />
          </div>

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Comisión (%)</span>
            </label>
            <input
              type="number"
              name="commission"
              value={formData.commission}
              onChange={handleInputChange}
              className="input input-bordered w-full"
              min="0"
              max="100"
              step="0.01"
            />
          </div>

          {!isTourismotion && (
            <div className="form-control w-full">
              <label className="label" htmlFor="platform-api-key">
                <span className="label-text">API Key</span>
              </label>
              <input
                id="platform-api-key"
                type="text"
                name="apiKey"
                value={formData.apiKey}
                onChange={handleInputChange}
                className="input input-bordered w-full"
              />
              <span className="label-text-alt mt-1">
                {formData.hasApiKey
                  ? 'Ya hay una clave guardada. Déjalo vacío para conservarla.'
                  : 'Opcional para plataformas que autentican mediante clave API.'}
              </span>
            </div>
          )}

          {isTourismotion && (
            <TourismotionCredentialsFields
              apiUsername={formData.apiUsername}
              apiPassword={formData.apiPassword}
              hasApiPassword={formData.hasApiPassword}
              onChange={handleInputChange}
            />
          )}

          <div className="form-control w-full">
            <label className="label">
              <span className="label-text">Color</span>
            </label>
            <input
              type="color"
              name="color"
              value={formData.color}
              onChange={handleInputChange}
              className="input input-bordered w-full h-10"
            />
          </div>

          {formData.supportsScheduler && (
            <>
              <div className="divider">Configuración del Scheduler</div>

              <div className="form-control">
                <label className="label cursor-pointer">
                  <span className="label-text">Habilitar Scheduler</span>
                  <input
                    type="checkbox"
                    name="schedulerEnabled"
                    checked={formData.schedulerEnabled}
                    onChange={handleInputChange}
                    className="toggle toggle-primary"
                  />
                </label>
              </div>

              {formData.schedulerEnabled && (
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text">Expresión Cron</span>
                  </label>
                  <input
                    type="text"
                    name="schedulerCron"
                    value={formData.schedulerCron}
                    onChange={handleInputChange}
                    className="input input-bordered w-full"
                    placeholder="0 */4 * * *"
                  />
                  <span className="label-text-alt mt-1">
                    Ejemplo: 0 */4 * * * (cada 4 horas)
                  </span>
                </div>
              )}
            </>
          )}

          <div className="btn-group">
            <button
              type="button"
              onClick={() => navigate('/platforms')}
              className="btn btn-outline"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                'Guardar Cambios'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformEditScreen; 
